import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Shield, Database, CreditCard, Eye, Clock, Lock, Settings, FileText, Phone } from 'lucide-react';
import HomeHeader2 from '@/components/homePage/HomeHeader2';

const COLORS = {
  orange: "#E87E04",
  white: "#FFFFFF",
  green: "#008751",
  darkGreen: "#006B3F",
  lightOrange: "#FFE0B2",
  lightGreen: "#CCFFCC",
  gray: "#718096",
  lightGray: "#E2E8F0",
  paleGreen: "#E6FFEA",
};

const PolitiqueConfidentialitePage = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  const sections = [
    {
      id: 1,
      title: "Données collectées",
      icon: <Database className="w-6 h-6" />,
      content: (
        <div>
          <p className="mb-4">Kassarmou collecte les données suivantes lors de l'utilisation du Site ou du passage de commande :</p>
          <ul className="list-disc pl-6 space-y-2 mb-4">
            <li>Nom et prénom</li>
            <li>Adresse postale (pour la livraison)</li>
            <li>Adresse e-mail</li>
            <li>Numéro de téléphone</li>
          </ul>
          <p>Ces informations sont nécessaires pour le traitement des commandes, la livraison, la communication avec le client et le service après-vente.</p>
        </div>
      )
    },
    {
      id: 2,
      title: "Données de paiement",
      icon: <CreditCard className="w-6 h-6" />,
      content: (
        <div>
          <p className="mb-4"><strong>Kassarmou ne collecte ni ne conserve aucune donnée de paiement.</strong></p>
          <p>Toutes les transactions sont sécurisées et traitées exclusivement via la plateforme Stripe. Aucune information de carte bancaire n'est stockée sur le Site.</p>
        </div>
      )
    },
    {
      id: 3,
      title: "Outils tiers et cookies",
      icon: <Settings className="w-6 h-6" />,
      content: (
        <div>
          <p className="mb-4">Le Site utilise des outils et services tiers susceptibles de collecter des données de navigation :</p>
          <ul className="list-disc pl-6 space-y-2 mb-4">
            <li><strong>Google Analytics</strong> : pour l'analyse du trafic et des performances du Site</li>
            <li><strong>WhatsApp</strong> : pour la communication directe avec les clients</li>
            <li><strong>Intégrations de réseaux sociaux</strong> : boutons ou liens vers Instagram et TikTok</li>
          </ul>
          <p className="mb-4">Le Site utilise également des cookies pour améliorer l'expérience utilisateur, mémoriser les préférences et mesurer l'audience.</p>
          <p>L'utilisateur peut gérer les paramètres de cookies via son navigateur.</p>
        </div>
      )
    },
    {
      id: 4,
      title: "Finalités de traitement",
      icon: <Eye className="w-6 h-6" />,
      content: (
        <div>
          <p className="mb-4">Les données collectées sont utilisées uniquement pour :</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Le traitement et la livraison des commandes</li>
            <li>La communication avec le client (suivi de commande, réponses aux demandes)</li>
            <li>Les statistiques internes (trafic du site, amélioration des services)</li>
          </ul>
        </div>
      )
    },
    {
      id: 5,
      title: "Conservation des données",
      icon: <Clock className="w-6 h-6" />,
      content: "Les données personnelles sont conservées uniquement pendant la durée nécessaire aux finalités mentionnées ci-dessus, sauf obligation légale contraire."
    },
    {
      id: 6,
      title: "Sécurité",
      icon: <Shield className="w-6 h-6" />,
      content: "Kassarmou s'engage à assurer la sécurité des données personnelles en mettant en œuvre des mesures techniques et organisationnelles appropriées."
    },
    {
      id: 7,
      title: "Droits des utilisateurs",
      icon: <Lock className="w-6 h-6" />,
      content: "Conformément au Règlement Général sur la Protection des Données (RGPD), l'utilisateur dispose d'un droit d'accès, de rectification, de suppression et de limitation du traitement de ses données. Pour exercer ces droits, l'utilisateur peut envoyer une demande via le formulaire de contact disponible sur le site kassarmoumarket.com."
    },
    {
      id: 8,
      title: "Modifications de la politique",
      icon: <FileText className="w-6 h-6" />,
      content: "Kassarmou se réserve le droit de modifier la présente politique de confidentialité à tout moment. En cas de mise à jour, la nouvelle version sera publiée sur cette page avec la date de dernière modification."
    },
    {
      id: 9,
      title: "Contact",
      icon: <Phone className="w-6 h-6" />,
      content: "Pour toute question relative à la politique de confidentialité, vous pouvez contacter Kassarmou via le formulaire de contact sur le site kassarmoumarket.com."
    }
  ];

  return (
    <>
    <HomeHeader2/>
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-green-50">
      {/* Header avec motif décoratif */}
      <div 
        className="relative overflow-hidden py-16 px-4"
        style={{ 
          background: `linear-gradient(135deg, ${COLORS.orange} 0%, ${COLORS.green} 100%)` 
        }}
      >
        {/* Motifs décoratifs */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-white"></div>
          <div className="absolute top-32 right-20 w-24 h-24 rounded-full bg-white"></div>
          <div className="absolute bottom-10 left-1/3 w-16 h-16 rounded-full bg-white"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="mb-6">
            <Shield className="w-16 h-16 text-white mx-auto mb-4" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Politique de Confidentialité
          </h1>
          <div className="w-24 h-1 bg-white mx-auto mb-6"></div>
          <p className="text-xl text-white/90 max-w-2xl mx-auto leading-relaxed">
            Protection et respect de vos données personnelles
          </p>
          <div className="mt-8 text-white/80">
            <p>kassarmoumarket.com</p>
          </div>
        </div>
      </div>

      {/* Introduction */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div 
          className="rounded-2xl p-8 mb-12 shadow-lg border-l-4"
          style={{ 
            backgroundColor: COLORS.paleGreen,
            borderLeftColor: COLORS.green 
          }}
        >
          <h2 className="text-2xl font-bold mb-4" style={{ color: COLORS.darkGreen }}>
            Notre engagement
          </h2>
          <p className="text-gray-700 leading-relaxed">
            La présente politique de confidentialité a pour but d'informer les utilisateurs du site 
            <strong> kassarmoumarket.com</strong> (ci-après "le Site") sur la manière dont leurs données 
            personnelles sont collectées, utilisées et protégées par l'entreprise <strong>Kassarmou</strong>, 
            entreprise individuelle dont le siège est situé en Belgique.
          </p>
        </div>

        {/* Sections accordéon */}
        <div className="space-y-4">
          {sections.map((section) => (
            <div 
              key={section.id}
              className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-lg"
            >
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full px-6 py-5 flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="flex items-center space-x-4">
                  <div 
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: COLORS.lightOrange }}
                  >
                    <div style={{ color: COLORS.orange }}>
                      {section.icon}
                    </div>
                  </div>
                  <div className="text-left">
                    <h3 className="text-lg font-semibold text-gray-800">
                      {section.id}. {section.title}
                    </h3>
                  </div>
                </div>
                <div style={{ color: COLORS.orange }}>
                  {expandedSection === section.id ? (
                    <ChevronUp className="w-6 h-6" />
                  ) : (
                    <ChevronDown className="w-6 h-6" />
                  )}
                </div>
              </button>
              
              {expandedSection === section.id && (
                <div className="px-6 pb-6">
                  <div className="pl-14">
                    <div 
                      className="h-px mb-4"
                      style={{ backgroundColor: COLORS.lightGray }}
                    ></div>
                    <div className="text-gray-700 leading-relaxed">
                      {typeof section.content === 'string' ? (
                        <p>{section.content}</p>
                      ) : (
                        section.content
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer de contact */}
        <div 
          className="mt-16 rounded-2xl p-8 text-center"
          style={{ 
            background: `linear-gradient(135deg, ${COLORS.lightGreen} 0%, ${COLORS.lightOrange} 100%)` 
          }}
        >
          <h3 className="text-2xl font-bold mb-4" style={{ color: COLORS.darkGreen }}>
            Questions sur vos données ?
          </h3>
          <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
            Pour toute question concernant cette politique de confidentialité ou pour 
            exercer vos droits RGPD, n'hésitez pas à nous contacter.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/ContactPage"
              className="px-6 py-3 rounded-lg font-semibold text-white transition-all duration-300 hover:scale-105 shadow-md text-center"
              style={{ backgroundColor: COLORS.green }}
            >
              Formulaire de contact
            </a>
            <a 
              href="https://wa.me/32465965436"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 rounded-lg font-semibold transition-all duration-300 hover:scale-105 shadow-md border-2 text-center"
              style={{ 
                color: COLORS.orange,
                borderColor: COLORS.orange,
                backgroundColor: 'white'
              }}
            >
              WhatsApp
            </a>
          </div>
        </div>

        {/* Date de dernière mise à jour */}
        <div className="text-center mt-8 text-gray-500">
          <p>Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
        </div>
      </div>
    </div>
    </>
  );
};

export default PolitiqueConfidentialitePage;